import React from 'react';
import {
  BarChart3, Phone, Users, Settings, TrendingUp, History, Volume2,
  Bot, Shield, Calendar, PhoneIncoming, Ticket, FileText, X,
  Package, CheckSquare, MessageSquare, ChevronLeft, ChevronRight,
} from 'lucide-react';

// Phase Audit Fix C: Workflows + IntegrationStatus pages removed from
// nav. They were demo-only surfaces (Workflows = "coming soon" stub;
// Integrations = mock connector grid) and shipping them as live links
// suggested capability we don't have. Re-add when the real builders
// land.
const DEFAULT_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'campaigns', label: 'Campaigns', icon: Phone },
  { id: 'leads', label: 'Leads', icon: Users },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
  { id: 'inbound-calls', label: 'Inbound Calls', icon: PhoneIncoming },
  { id: 'tickets', label: 'Tickets', icon: Ticket },
  { id: 'agents', label: 'AI Agents', icon: Bot },
  { id: 'voice-library', label: 'Voice Library', icon: Volume2 },
  { id: 'call-history', label: 'Call History', icon: History },
  { id: 'tasks', label: 'Tasks', icon: CheckSquare },
  { id: 'sms-automation', label: 'SMS Automation', icon: MessageSquare },
  { id: 'inventory', label: 'Inventory', icon: Package },
  { id: 'analytics', label: 'Analytics', icon: TrendingUp },
  { id: 'reports', label: 'Reports', icon: FileText },
  { id: 'admin', label: 'Admin', icon: Shield },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const PRIMARY_IDS = new Set([
  'dashboard', 'campaigns', 'leads', 'calendar', 'inbound-calls',
  'call-history', 'tickets', 'tasks', 'sms-automation', 'inventory',
  'agents', 'voice-library',
]);
const ORG_IDS = new Set([
  'analytics', 'reports', 'admin', 'settings',
]);

const Sidebar = ({
  items,
  currentPage,
  setCurrentPage,
  isOpen,
  onClose,
  collapsed = false,
  onToggleCollapse,
}) => {
  const menuItems = items && items.length ? items : DEFAULT_ITEMS;
  const primary = menuItems.filter((i) => PRIMARY_IDS.has(i.id));
  const org = menuItems.filter((i) => ORG_IDS.has(i.id));

  const handleNavClick = (pageId) => {
    setCurrentPage(pageId);
    if (onClose) onClose();
  };

  const widthClass = collapsed ? 'w-16' : 'w-60';

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}

      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          ${widthClass} border-r border-ink-200/70 dark:border-ink-800/70
          bg-white/70 dark:bg-ink-950/60 backdrop-blur-md
          flex flex-col
          transform transition-all duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="sidebar"
        aria-label="Primary navigation"
      >
        {/* Header — section label + collapse toggle + mobile close */}
        <div className="h-12 flex items-center justify-between px-3 border-b border-ink-200/60 dark:border-ink-800/60">
          {!collapsed ? (
            <span className="text-[10px] uppercase tracking-[0.18em] text-ink-500 font-medium">
              Workspace
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.18em] text-ink-500 font-medium w-full text-center">·</span>
          )}
          <div className="flex items-center gap-1">
            {onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                className="hidden lg:flex w-7 h-7 items-center justify-center rounded-md text-ink-500 hover:bg-ink-100/60 dark:hover:bg-ink-800/60 focus-2050"
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                data-testid="sidebar-collapse-toggle"
              >
                {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
              </button>
            )}
            <button
              onClick={onClose}
              className="lg:hidden w-7 h-7 flex items-center justify-center rounded-md text-ink-500 hover:bg-ink-100/60 dark:hover:bg-ink-800/60"
              data-testid="sidebar-close"
              aria-label="Close sidebar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5 stagger" aria-label="Workspace">
          {primary.map((item) => renderItem(item, currentPage, handleNavClick, collapsed))}

          {org.length > 0 && (
            <>
              <div className={`px-3 pt-4 pb-2 mt-2 border-t border-ink-200/60 dark:border-ink-800/60 ${collapsed ? 'text-center' : ''}`}>
                {!collapsed && (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-ink-500 font-medium">
                    Org
                  </span>
                )}
              </div>
              {org.map((item) => renderItem(item, currentPage, handleNavClick, collapsed))}
            </>
          )}
        </nav>

        <div className="p-3 border-t border-ink-200/60 dark:border-ink-800/60">
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2'}`}>
            <span className="pulse-dot" aria-hidden="true" />
            {!collapsed && <span className="text-xs text-ink-500">System online</span>}
          </div>
        </div>
      </aside>
    </>
  );
};

function renderItem(item, currentPage, onClick, collapsed) {
  const Icon = item.icon;
  const isActive = currentPage === item.id;
  return (
    <button
      key={item.id}
      onClick={() => onClick(item.id)}
      title={collapsed ? item.label : undefined}
      aria-current={isActive ? 'page' : undefined}
      className={
        `w-full flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2 ` +
        `rounded-md text-sm font-medium transition-colors relative focus-2050 ` +
        (isActive
          ? 'nav-active-2050'
          : 'text-ink-700 dark:text-ink-200 hover:bg-ink-100/60 dark:hover:bg-ink-800/60')
      }
      data-testid={`nav-${item.id}`}
    >
      <Icon className={`w-4 h-4 ${isActive ? 'text-gold' : 'text-ink-500'} flex-shrink-0`} />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </button>
  );
}

export default Sidebar;
